import type { GridStack, GridStackNode, GridStackOptions, GridStackWidget } from 'gridstack';
import { type PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import { GridStackContext } from './grid-stack-context';
import { useAppDispatch, useAppSelector } from '../../../redux/ReduxHooks';
import { projectSlice } from '../../../redux/ProjectSlice';
import { ApplicationActions } from '../../../utils/constants';
import {
  registerDashboardLayoutMetaMapSync,
  registerDashboardMetaLayoutGetter,
  syncRawWidgetMetaMapFromLayout,
} from '../persistDashboardWidgetContent';
import { normalizeWidgetSizeConstraints } from '../../../utils/dashboardWidgetSizeUtils';
import { stableJsonSignature } from '../../../utils/stableJsonSignature';

export function GridStackProvider({ children, initialOptions }: PropsWithChildren<{ initialOptions: GridStackOptions }>) {
  const dispatch = useAppDispatch();
  const dashboardViewMode = useAppSelector((state) => state.application.dashboardViewMode);
  const isLiveMode = dashboardViewMode === ApplicationActions.DashboardViewMode.LIVE;
  const [isLoaded, setIsLoaded] = useState(false);
  const [gridStack, setGridStack] = useState<GridStack | null>(null);
  const prevLayoutRef = useRef<string | null>(null);

  const rawWidgetMetaMapRef = useRef<Map<string, GridStackWidget>>(new Map());

  const [rawWidgetMetaMap, setRawWidgetMetaMap] = useState(() => {
    const map = new Map<string, GridStackWidget>();
    const deepFindNodeWithContent = (obj: GridStackWidget) => {
      if (obj.id && obj.content) {
        map.set(obj.id, obj);
      }
      if (obj.subGridOpts?.children) {
        obj.subGridOpts.children.forEach((child: GridStackWidget) => {
          deepFindNodeWithContent(child);
        });
      }
    };
    initialOptions.children?.forEach((child: GridStackWidget) => {
      deepFindNodeWithContent(child);
    });
    rawWidgetMetaMapRef.current = map;
    return map;
  });

  rawWidgetMetaMapRef.current = rawWidgetMetaMap;

  useEffect(() => {
    registerDashboardLayoutMetaMapSync((layout) => {
      syncRawWidgetMetaMapFromLayout(setRawWidgetMetaMap, layout);
    });
    return () => registerDashboardLayoutMetaMapSync(undefined);
  }, []);

  useEffect(() => {
    registerDashboardMetaLayoutGetter(() => Array.from(rawWidgetMetaMapRef.current.values()));
    return () => registerDashboardMetaLayoutGetter(undefined);
  }, []);

  const addWidget = useCallback(
    (fn: (id: string) => Omit<GridStackWidget, 'id'>) => {
      const newId = `widget-${Math.random().toString(36).substring(2, 15)}`;
      const widget = fn(newId);
      gridStack?.addWidget({ ...widget, id: newId });

      setRawWidgetMetaMap((prev) => {
        const newMap = new Map<string, GridStackWidget>(prev);
        newMap.set(newId, widget);
        return newMap;
      });
    },
    [gridStack]
  );

  const addSubGrid = useCallback(
    (fn: (id: string, withWidget: (w: Omit<GridStackWidget, 'id'>) => GridStackWidget) => Omit<GridStackWidget, 'id'>) => {
      const newId = `sub-grid-${Math.random().toString(36).substring(2, 15)}`;
      const subWidgetIdMap = new Map<string, GridStackWidget>();

      const widget = fn(newId, (w) => {
        const subWidgetId = `widget-${Math.random().toString(36).substring(2, 15)}`;
        subWidgetIdMap.set(subWidgetId, w);
        return { ...w, id: subWidgetId };
      });

      gridStack?.addWidget({ ...widget, id: newId });

      setRawWidgetMetaMap((prev) => {
        const newMap = new Map<string, GridStackWidget>(prev);
        subWidgetIdMap.forEach((meta, id) => {
          newMap.set(id, meta);
        });
        return newMap;
      });
    },
    [gridStack]
  );

  const removeWidget = useCallback(
    (id: string) => {
      const el = document.querySelector(`.grid-stack-item[gs-id="${id}"]`);

      if (el && gridStack) {
        gridStack.removeWidget(el as HTMLElement);
      }
      setRawWidgetMetaMap((prev) => {
        const newMap = new Map<string, GridStackWidget>(prev);
        newMap.delete(id);
        return newMap;
      });
    },
    [gridStack]
  );

  const saveOptions = useCallback(() => {
    return gridStack?.save(true, true, (_, widget) => widget);
  }, [gridStack]);

  const loadWidgets = useCallback(
    (widgets: GridStackWidget[]) => {
      if (!gridStack) return;

      const normalizedWidgets = widgets.map((widget) => normalizeWidgetSizeConstraints(widget));

      gridStack.load(normalizedWidgets);

      const newMap = new Map<string, GridStackWidget>();
      normalizedWidgets.forEach((widget) => widget.id && newMap.set(widget.id, widget));
      prevLayoutRef.current = stableJsonSignature(Array.from(newMap.entries()));
      setRawWidgetMetaMap(newMap);
      setIsLoaded(true);
    },
    [gridStack]
  );

  const handleGridChange = useCallback(
    (event?: Event, items?: GridStackNode[]) => {
      if (!gridStack) return;

      setRawWidgetMetaMap((prev) => {
        const newMap = new Map(prev);

        if (event?.type === 'removed' && items) {
          items.forEach((item) => {
            if (item.id) {
              newMap.delete(item.id);
            }
          });
        } else {
          gridStack.engine.nodes.forEach((node) => {
            if (node.id) {
              const prevWidget = prev.get(node.id);
              newMap.set(node.id, {
                ...prevWidget,
                id: node.id,
                x: node.x,
                y: node.y,
                w: node.w,
                h: node.h,
                minH: node.minH ?? prevWidget?.minH,
                minW: node.minW ?? prevWidget?.minW,
                maxH: node.maxH ?? prevWidget?.maxH,
                maxW: node.maxW ?? prevWidget?.maxW,
                content: prevWidget?.content ?? '',
              });
            }
          });
        }

        return newMap;
      });
    },
    [gridStack]
  );

  const prevIsLiveModeRef = useRef(isLiveMode);

  useEffect(() => {
    if (!isLoaded || !isLiveMode) {
      prevIsLiveModeRef.current = isLiveMode;
      return;
    }

    const justEnteredLive = !prevIsLiveModeRef.current;
    prevIsLiveModeRef.current = isLiveMode;

    // Historic layout stays in rawWidgetMetaMap until Dashboard reloads live widgets.
    // Skip one persist cycle so we do not write the historic snapshot into the project.
    if (justEnteredLive) {
      return;
    }

    const currentLayout = stableJsonSignature(Array.from(rawWidgetMetaMap.entries()));

    if (prevLayoutRef.current !== currentLayout) {
      prevLayoutRef.current = currentLayout;
      dispatch(projectSlice.actions.onSaveLayout(Array.from(rawWidgetMetaMap.values())));
    }
  }, [rawWidgetMetaMap, isLoaded, dispatch, isLiveMode]);

  // Save the layout when anything is changed on gridStack
  useEffect(() => {
    if (!gridStack) return;

    gridStack.on('added change removed', handleGridChange);

    return () => {
      gridStack.off('added change removed');
    };
  }, [gridStack, handleGridChange]);

  return (
    <GridStackContext.Provider
      value={{
        initialOptions,
        gridStack,

        addWidget,
        removeWidget,
        addSubGrid,
        saveOptions,
        loadWidgets,

        _gridStack: {
          value: gridStack,
          set: setGridStack,
        },
        _rawWidgetMetaMap: {
          value: rawWidgetMetaMap,
          set: setRawWidgetMetaMap,
        },
      }}
    >
      {children}
    </GridStackContext.Provider>
  );
}
